import styled from 'styled-components';

// Container matching the HTML structure
export const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;

  @media (max-width: 768px) {
    padding: 0;
    max-width: 100%;
    width: 100%;
    overflow-x: hidden;
  }
`;

// Dashboard Layout
export const DashboardLayout = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 0.5fr;
  gap: 24px;
  align-items: start;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    display: block;
    width: 100%;
  }
`;

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const Sidebar = styled.div`
  position: sticky;
  top: 20px;

  @media (max-width: 768px) {
    position: static;
    width: 100%;
    margin-top: 16px;
  }
`;

// Card base style - matching HTML exactly
// Card component has been moved to shared components/Card

// Header Container - matches HTML structure
export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    align-items: center;
    gap: 12px;
  }
`;

// Icon Wrapper with orange background
export const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(250, 70, 22, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    border-radius: 8px;

    svg {
      width: 20px !important;
      height: 20px !important;
    }
  }
`;

export const HeaderContent = styled.div`
  flex: 1;
`;

export const Title = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.primary
      : props.theme.colors.blueBlack};
  letter-spacing: -0.5px;
  margin: 0;
  line-height: 1.2;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;

  @media (max-width: 768px) {
    font-size: 22px;
    line-height: 28px;
    margin: 0;
  }
`;

export const Subtitle = styled.p`
  font-size: 17px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.muted};
  line-height: 1.6;
  margin: 0;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;

  @media (max-width: 768px) {
    display: none;
  }
`;

// Smart Insights Component - Left Border Design
// SmartInsights has been replaced with shared Card component with custom styling

export const InsightsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

export const InsightsTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const InsightsText = styled.div`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.dark
      : props.theme.colors.textGray.disabled};
  line-height: 1.4;
  margin-bottom: 4px;
`;

export const InsightsSubtext = styled.div`
  font-size: 13px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.muted};
  line-height: 1.4;
`;

export const LearnMore = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  margin-top: 8px;
  transition: opacity 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

export const ExpandButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #64748b;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  }
`;

// AlertBox has been replaced with shared Card component
// export const AlertBox = SmartInsights; // Deprecated - use Card instead
export const AlertTitle = InsightsTitle;
export const AlertText = InsightsText;

// Metrics Grid
export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 30px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    overflow-y: visible;
    gap: 10px;
    margin: 0 0 16px 0;
    padding: 0 8px 8px 16px;
    width: 100%;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

// Metric Card clean design
export const MetricCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};
  border-left: 4px solid ${(props) => props.theme?.colors?.primary || '#FA4616'};
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 100px;

  ${(props) =>
    props.featured &&
    `
    background: ${
      props.theme.name === 'dark'
        ? 'linear-gradient(135deg, rgba(250,70,22,0.15) 0%, rgba(250,70,22,0.05) 100%)'
        : 'linear-gradient(135deg, rgba(250,70,22,0.05) 0%, rgba(250,70,22,0.02) 100%)'
    };
    border: 1px solid ${
      props.theme.name === 'dark'
        ? 'rgba(250,70,22,0.5)'
        : 'rgba(250,70,22,0.3)'
    };
  `}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px
      rgba(
        0,
        0,
        0,
        ${(props) => (props.theme.name === 'dark' ? '0.3' : '0.15')}
      );
    ${(props) =>
      props.featured &&
      `
      box-shadow: 0 4px 12px rgba(250,70,22,0.3);
    `}
  }

  @media (max-width: 768px) {
    min-width: 140px;
    padding: 20px 12px;
    flex-shrink: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    min-height: auto;
    height: auto;
  }
`;

export const MetricContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  gap: 2px;
`;

export const MetricValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.primary
      : props.theme.colors.blueBlack};
  margin: 4px 0;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 24px;
    margin: 2px 0;
  }
`;

export const MetricLabel = styled.div`
  font-size: 13px;
  letter-spacing: 0.03em;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.muted};
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: visible;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 12px;
    letter-spacing: 0.02em;
    line-height: 1.2;
    margin-bottom: 2px;
  }
`;

export const MetricIcon = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  width: 36px;
  height: 100%;
  align-self: stretch;
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 768px) {
    width: 32px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const MetricChange = styled.div`
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  color: ${(props) =>
    props.positive ? props.theme?.colors?.success || '#22C55E' : '#ef4444'};
  font-weight: 500;
  margin-top: 4px;

  svg {
    width: 10px;
    height: 10px;
  }

  @media (max-width: 768px) {
    font-size: 11px;
    margin-top: 2px;
    gap: 2px;

    svg {
      width: 10px;
      height: 10px;
    }
  }
`;
